import { useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { VivintEligibilityResponse } from "cap/capTypes";
import { useAuthToken } from "hooks/useAuthToken";
import { rollbar } from "providers/rollbar";
import { checkVSHEligibility } from "services/vivintEligibility";
import { ConfirmInfoOptionalTask } from "components/molecules/ConfirmInfo/ConfirmInfoOptionalTask";

interface VivintOptionalTaskCardProps {
  leadSource?: string;
  isPartner?: boolean;
}

const VivintOptionalTaskCard = ({
  leadSource,
  isPartner,
}: VivintOptionalTaskCardProps) => {
  const { prospectId } = useParams();
  const authToken = useAuthToken("IHD");
  const [vivintData, setVivintData] = useState<
    VivintEligibilityResponse | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const isVivintLeadSource = leadSource === "Vivint Smarthome";

  const checkEligibility = useCallback(() => {
    // If leadSource is "Vivint Smarthome", skip the eligibility check
    if (isVivintLeadSource) {
      setLoading(false);
      setError(false);
      return;
    }

    if (!authToken?.token || !prospectId) {
      return;
    }
    setLoading(true);
    setError(false);
    checkVSHEligibility(authToken.token, prospectId)
      .then((vshData) => {
        setVivintData(vshData);
      })
      .catch((error) => {
        rollbar.error(error);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authToken, prospectId, isVivintLeadSource]);

  useEffect(() => checkEligibility(), [checkEligibility]);

  // If leadSource is "Vivint Smarthome", always show as eligible
  if (isVivintLeadSource) {
    return (
      <ConfirmInfoOptionalTask
        title="Vivint Smart Home"
        body={{ text: "Discount eligible", type: "success" }}
        loading={false}
        link="https://s.tiled.co/2G6ONl5/vivint-fast-pitch-2025"
        linkText="Vivint Details"
      />
    );
  }

  if (isPartner) return null;

  const { isEligible, zipcodeIsEligble } = vivintData?.eligibility || {};
  const body: { text: string; type: "success" | "problem" } = isEligible
    ? { text: "Eligible", type: "success" }
    : { text: "Not eligible", type: "problem" };
  const eligibilityText = zipcodeIsEligble
    ? "Due to previous relationship"
    : "Due to zip code";

  if (error) {
    return (
      <ConfirmInfoOptionalTask
        title="Vivint Smart Home"
        body={{ text: "Error checking eligibility", type: "problem" }}
        loading={false}
        onLaunch={checkEligibility}
        onLaunchText="Retry"
      />
    );
  }

  return (
    <ConfirmInfoOptionalTask
      title="Vivint Smart Home"
      body={body}
      additionalText={isEligible ? undefined : eligibilityText}
      link={
        isEligible
          ? "https://s.tiled.co/2G6ONl5/vivint-fast-pitch-2025"
          : undefined
      }
      linkText="Vivint Details"
      loading={!vivintData || loading}
    ></ConfirmInfoOptionalTask>
  );
};

export { VivintOptionalTaskCard };
